import React from 'react';
import { FormProvider, useForm, WatchObserver } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Layout, Typography } from 'antd';

import { useQuery } from 'hooks/useQuery';
import { InputField } from 'components/form/base/InputField';
import { Statuses } from 'constants/status';
import { Select } from 'components/form/base/Select';
import { AuthorAsyncSelect } from 'components/form/selects/AuthorAsyncSelect';
import { debounce, removeEmptyValues } from 'utils/helpers';
import { mapSelect } from 'utils/mappings';
import { LangEnumKeys } from 'constants/lang';
import { LocaleSelect } from 'components/form/selects/LocaleSelect';
import { CatalogAsyncSelect } from 'components/form/selects/CatalogAsyncSelect';

import { FILTER_STATUS_OPTIONS } from './constants';

type TFormSelectValue = { value: string; label: string };

export interface FilterValues {
  name: string;
  status: Statuses | '';
  authors: string[] | null;
  categories: string[] | TFormSelectValue[];
  lang: LangEnumKeys | { value: LangEnumKeys; label: string } | '';
}

export const Filters = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const methods = useForm<FilterValues>({
    defaultValues: {
      name: '',
      authors: [],
      status: '',
      categories: [],
      lang: '',
      ...query,
    },
  });

  const updateFilters = (filterValues: Record<string, string>) => {
    const queryString = new URLSearchParams(filterValues).toString();
    navigate(`${location.pathname}?${queryString}`);
  };

  const debounceSubmit = React.useCallback(
    debounce(({ authors, status, categories, ...values }: FilterValues) => {
      const preparedValues = {
        ...values,
        categories: categories.length ? mapSelect(categories) : null,
        status: mapSelect(status),
        authors: authors?.length ? mapSelect(authors) : null,
        lang: mapSelect(values.lang),
      };
      updateFilters(removeEmptyValues(preparedValues));
    }, 500),
    []
  );

  React.useEffect(() => {
    const subscription = methods.watch(
      methods.handleSubmit(
        debounceSubmit
      ) as unknown as WatchObserver<FilterValues>
    );
    return () => subscription.unsubscribe();
  }, [methods.handleSubmit, methods.watch]);

  return (
    <Layout.Content
      style={{ background: '#fff', paddingTop: 16, paddingBottom: 1 }}
    >
      <Typography.Title level={5} style={{ marginBottom: 24 }}>
        Фильтры
      </Typography.Title>
      <FormProvider {...methods}>
        <form className='ant-form-vertical'>
          <InputField
            name='name'
            placeholder='Введите название'
            label='Название организации'
            maxLength={255}
          />
          <Select
            name='status'
            options={FILTER_STATUS_OPTIONS}
            label='Статус'
          />
          <CatalogAsyncSelect
            name='categories'
            label='Территория ОЭЗ'
            requestParams={{
              entityType: 'oezTerritory',
              fieldType: 'oezname',
              lang: query.lang as LangEnumKeys,
            }}
            mode='multiple'
            placeholder='Выберите ОЭЗ'
          />
          <AuthorAsyncSelect
            name='authors'
            label='Автор'
            placeholder='Выберите автора'
            mode='multiple'
          />
          <LocaleSelect
            name='lang'
            label='Язык'
            placeholder='Язык'
            withEmptyValue
          />
        </form>
      </FormProvider>
    </Layout.Content>
  );
};
